import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { setDoc, Timestamp } from 'firebase/firestore';
import { checkSignUpRateLimit } from '../utils/rateLimitService';
import { withTimeout } from '../utils/withTimeout';
import { getAuthErrorMessage } from '../utils/authErrorMessage';
import { TERMS_VERSION } from './TermsOfService';
import { PRIVACY_VERSION } from './PrivacyPolicy';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { userProfileDocument } from '../lib/paths';

// Sign-up waits on an App Check token. If that token never arrives the request
// is never sent and the form would sit disabled indefinitely with nothing to
// show, so bound it and surface a real error instead.
const SIGN_UP_TIMEOUT_MS = 25000;
const TIMEOUT_MESSAGE =
  'Połączenie z serwerem trwa zbyt długo. Sprawdź połączenie z internetem i spróbuj ponownie.';

const Register: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Hasła nie są identyczne.');
      return;
    }

    if (password.length < 6) {
      setError('Hasło musi mieć co najmniej 6 znaków.');
      return;
    }

    if (!acceptedTerms) {
      setError('Musisz zaakceptować regulamin i politykę prywatności.');
      return;
    }

    setLoading(true);

    try {
      const limitCheck = await checkSignUpRateLimit(email);
      if (!limitCheck.allowed) {
        setError(
          limitCheck.message ||
            'Zbyt wiele prób rejestracji. Spróbuj ponownie później.'
        );
        return;
      }

      const userCredential = await withTimeout(
        createUserWithEmailAndPassword(auth, email, password),
        SIGN_UP_TIMEOUT_MS,
        TIMEOUT_MESSAGE
      );
      const user = userCredential.user;

      // Create user profile with terms acceptance (pending subscription)
      await setDoc(userProfileDocument(user.uid), {
        email: user.email,
        createdAt: Timestamp.now(),
        termsAcceptedAt: Timestamp.now(),
        termsVersion: TERMS_VERSION,
        privacyAcceptedAt: Timestamp.now(),
        privacyVersion: PRIVACY_VERSION,
        subscriptionStatus: 'pending',
        trialEndsAt: null,
        stripeCustomerId: null,
        stripeSubscriptionId: null,
        currentPeriodEnd: null,
        cancelAtPeriodEnd: false,
      });

      navigate('/onboarding/company-details');
    } catch (err: unknown) {
      setError(
        getAuthErrorMessage(
          err,
          'Nie udało się utworzyć konta. Spróbuj ponownie.'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-5">
        <div className="text-center">
          <img
            src="/icon-192x192.png"
            alt="Złomiarz Logo"
            className="mx-auto h-14 w-14 object-contain rounded-lg shadow-lg"
          />
          <h2 className="mt-3 text-2xl font-bold text-gray-900">
            Utwórz konto
          </h2>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-700 bg-red-100 border-l-4 border-red-500 rounded">
                {error}
              </div>
            )}
            <div className="space-y-1">
              <label
                className="block text-gray-700 text-sm font-medium"
                htmlFor="email"
              >
                Email
              </label>
              <input
                className="w-full px-3 py-2 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent transition duration-200"
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                placeholder="Wprowadź swój email"
                autoComplete="email"
              />
            </div>
            <div className="space-y-1">
              <label
                className="block text-gray-700 text-sm font-medium"
                htmlFor="password"
              >
                Hasło
              </label>
              <input
                className="w-full px-3 py-2 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent transition duration-200"
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                placeholder="Minimum 6 znaków"
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-1">
              <label
                className="block text-gray-700 text-sm font-medium"
                htmlFor="confirmPassword"
              >
                Potwierdź hasło
              </label>
              <input
                className="w-full px-3 py-2 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent transition duration-200"
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                placeholder="Powtórz hasło"
                autoComplete="new-password"
              />
            </div>
            <div className="flex items-start">
              <input
                id="terms"
                type="checkbox"
                checked={acceptedTerms}
                onChange={e => setAcceptedTerms(e.target.checked)}
                className="mt-1 h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
              />
              <label htmlFor="terms" className="ml-2 text-sm text-gray-600">
                Akceptuję{' '}
                <Link
                  to="/terms"
                  className="text-orange-700 hover:text-orange-800 underline"
                  target="_blank"
                >
                  regulamin
                </Link>{' '}
                i{' '}
                <Link
                  to="/privacy"
                  className="text-orange-700 hover:text-orange-800 underline"
                  target="_blank"
                >
                  politykę prywatności
                </Link>
              </label>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-700 hover:bg-orange-800 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-base font-semibold py-2.5 px-6 rounded-lg transition duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-orange-600 focus:ring-offset-2"
            >
              {loading ? 'Rejestracja...' : 'Zarejestruj się'}
            </button>
          </form>
          <div className="mt-4 flex items-center">
            <div className="flex-grow border-t border-gray-300" />
            <span className="mx-4 text-sm text-gray-500">lub</span>
            <div className="flex-grow border-t border-gray-300" />
          </div>
          <div className="mt-4">
            <GoogleSignInButton
              label="Zarejestruj się przez Google"
              onSuccess={() => navigate('/dashboard')}
              onError={setError}
            />
            <p className="mt-2 text-xs text-gray-500 text-center">
              Kontynuując przez Google, akceptujesz{' '}
              <Link to="/terms" className="underline hover:text-gray-700">
                regulamin
              </Link>{' '}
              i{' '}
              <Link to="/privacy" className="underline hover:text-gray-700">
                politykę prywatności
              </Link>
              .
            </p>
          </div>
          <p className="mt-4 text-center text-sm text-gray-600">
            Masz już konto?{' '}
            <Link
              to="/login"
              className="text-orange-700 hover:text-orange-800 font-medium"
            >
              Zaloguj się
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
